import React, { useState, useMemo, useEffect, useRef } from 'react';
import { SessionMemoryEntry } from '@/types/sessionMemory.types';
import { X, Search, Trash2, Clock, ChevronRight } from 'lucide-react';
import { Button, Badge } from '../ui';

interface SessionMemoryPanelProps {
  isOpen: boolean;
  onClose: () => void;
  entries: SessionMemoryEntry[];
  onRecall: (entry: SessionMemoryEntry) => void;
  onDelete: (id: string) => void;
  onSaveCurrent: () => void;
}

export const SessionMemoryPanel: React.FC<SessionMemoryPanelProps> = ({
  isOpen,
  onClose,
  entries,
  onRecall,
  onDelete,
  onSaveCurrent
}) => {
  const [search, setSearch] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const filtered = useMemo(() => {
    if (!search.trim()) return entries;
    const q = search.toLowerCase();
    return entries.filter(e =>
      e.name.toLowerCase().includes(q) ||
      e.description.toLowerCase().includes(q) ||
      e.tags.some(t => t.toLowerCase().includes(q)) ||
      e.preview.vocalPackName.toLowerCase().includes(q) ||
      e.preview.blends.some(b => b.toLowerCase().includes(q))
    );
  }, [entries, search]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Tab' && panelRef.current) {
        const focusable = panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[80] flex">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Session Memory"
        className="relative ml-auto w-full max-w-md bg-slate-950 border-l border-slate-800 shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-800">
          <div>
            <h2 className="text-sm font-black text-slate-200 uppercase tracking-tight">Session Memory</h2>
            <p className="text-[11px] text-slate-400 mt-0.5">{entries.length} saved session{entries.length !== 1 ? 's' : ''}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="primary" size="xs" onClick={onSaveCurrent} className="!text-[11px] !font-black !uppercase">
              Save Current
            </Button>
            <button onClick={onClose} className="text-slate-400 hover:text-slate-300 transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="p-4 border-b border-slate-800/50">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search sessions..."
              className="w-full bg-slate-900 border border-slate-800 rounded-xl pl-9 pr-3 py-2.5 text-xs text-slate-300 placeholder:text-slate-400 focus:outline-none focus:border-accent/50"
            />
          </div>
        </div>

        {/* Entries List */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {filtered.length === 0 && (
            <div className="text-center py-12 text-slate-400">
              <Clock size={32} className="mx-auto mb-3 opacity-30" />
              <p className="text-xs font-bold uppercase tracking-wider">
                {entries.length === 0 ? 'No saved sessions yet' : 'No matching sessions'}
              </p>
              <p className="text-[11px] mt-1">
                {entries.length === 0 ? 'Configure your workspace and click "Save Current"' : 'Try a different search term'}
              </p>
            </div>
          )}

          {filtered.map(entry => (
            <div
              key={entry.id}
              className="bg-slate-900/60 border border-slate-800 rounded-2xl p-4 hover:border-accent/30 transition-all group"
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{entry.icon}</span>
                <div className="flex-1 min-w-0">
                  <h4 className="text-xs font-black text-slate-200 uppercase tracking-tight truncate">{entry.name}</h4>
                  {entry.description && (
                    <p className="text-[11px] text-slate-400 mt-0.5 line-clamp-2">{entry.description}</p>
                  )}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <Badge variant="info-muted" size="xs" className="!text-[11px]">{entry.preview.vocalPackName}</Badge>
                    {entry.preview.blends.slice(0, 2).map(b => (
                      <Badge key={b} variant="default-muted" size="xs" className="!text-[11px]">{b}</Badge>
                    ))}
                    {entry.preview.hasOutput && (
                      <Badge variant="success-muted" size="xs" className="!text-[11px]">Has Output</Badge>
                    )}
                  </div>
                  {entry.preview.seedPreview && (
                    <p className="text-[11px] text-slate-400 mt-1.5 italic truncate">"{entry.preview.seedPreview}"</p>
                  )}
                  {entry.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {entry.tags.map(t => (
                        <span key={t} className="text-[11px] text-slate-400 bg-slate-800/50 px-1.5 py-0.5 rounded-md">#{t}</span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-800/50">
                <span className="text-[11px] text-slate-400 font-mono">
                  {new Date(entry.createdAt).toLocaleDateString()}
                </span>
                <div className="flex items-center gap-2">
                  {confirmDeleteId === entry.id ? (
                    <>
                      <Button
                        variant="danger"
                        size="xs"
                        onClick={() => { onDelete(entry.id); setConfirmDeleteId(null); }}
                        className="!text-[11px] !font-black !uppercase"
                      >
                        Confirm
                      </Button>
                      <Button
                        variant="secondary"
                        size="xs"
                        onClick={() => setConfirmDeleteId(null)}
                        className="!text-[11px]"
                      >
                        Cancel
                      </Button>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={() => setConfirmDeleteId(entry.id)}
                        className="text-slate-400 hover:text-red-400 transition-colors"
                      >
                        <Trash2 size={12} />
                      </button>
                      <Button
                        variant="primary"
                        size="xs"
                        onClick={() => onRecall(entry)}
                        className="!text-[11px] !font-black !uppercase flex items-center gap-1"
                      >
                        Recall <ChevronRight size={10} />
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
